import { createRoot, createSignal, onCleanup } from "solid-js";

// Helper function to retrieve stored data from localStorage
const getStoredValue = (key, defaultValue) => {
  if (typeof window === "undefined") return defaultValue;

  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : defaultValue;
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return defaultValue;
  }
};

// Create the store within a reactive root
function createUserStore() {
  return createRoot((dispose) => {
    // Initialize signals with values from localStorage if available
    const [userEmail, setUserEmail] = createSignal(
      getStoredValue("userEmail", null),
    );
    const [userInterviews, setUserInterviews] = createSignal(
      getStoredValue("userInterviews", null),
    );
    const [userAnswers, setUserAnswers] = createSignal(
      getStoredValue("userAnswers", Array(10).fill(null)),
    );
    const [userFeedback, setUserFeedback] = createSignal(
      getStoredValue("userFeedback", null),
    );
    const [interview, setInterview] = createSignal(
      getStoredValue("interview", null),
    );
    const [token, setToken] = createSignal(getStoredValue("token", null));
    const [region, setRegion] = createSignal(getStoredValue("region", null));
    const [playState, setPlayState] = createSignal(
      getStoredValue("playState", false),
    );

    // Custom setter functions that update localStorage
    const persistedSetUserEmail = (value) => {
      setUserEmail(value);
      if (value) {
        localStorage.setItem("userEmail", JSON.stringify(value));
      } else {
        localStorage.removeItem("userEmail");
      }
    };

    const persistedSetUserInterviews = (value) => {
      setUserInterviews(value);
      if (value) {
        localStorage.setItem("userInterviews", JSON.stringify(value));
      } else {
        localStorage.removeItem("userInterviews");
      }
    };

    const persistedSetUserAnswers = (value) => {
      setUserAnswers(value);
      if (value) {
        localStorage.setItem("userAnswers", JSON.stringify(value));
      } else {
        localStorage.removeItem("userAnswers");
      }
    };

    const persistedSetUserFeedback = (value) => {
      setUserFeedback(value);
      if (value) {
        localStorage.setItem("userFeedback", JSON.stringify(value));
      } else {
        localStorage.removeItem("userFeedback");
      }
    };

    const persistedSetInterview = (value) => {
      setInterview(value);
      if (value) {
        localStorage.setItem("interview", JSON.stringify(value));
      } else {
        localStorage.removeItem("interview");
      }
    };

    const persistedSetToken = (value) => {
      setToken(value);
      if (value) {
        localStorage.setItem("token", JSON.stringify(value));
      } else {
        localStorage.removeItem("token");
      }
    };

    const persistedSetRegion = (value) => {
      setRegion(value);
      if (value) {
        localStorage.setItem("region", JSON.stringify(value));
      } else {
        localStorage.removeItem("region");
      }
    };

    const persistedSetPlayState = (value) => {
      setPlayState(value);
      localStorage.setItem("playState", JSON.stringify(value));
    };

    // Function to clear all user data from localStorage (for logout)
    const clearUserStore = () => {
      localStorage.removeItem("userEmail");
      localStorage.removeItem("userInterviews");
      localStorage.removeItem("userAnswers");
      localStorage.removeItem("userFeedback");
      localStorage.removeItem("interview");
      localStorage.removeItem("token");
      localStorage.removeItem("region");
      localStorage.removeItem("playState");

      // Reset all signals to their default values
      setUserEmail(null);
      setUserInterviews(null);
      setUserAnswers(null);
      setUserFeedback(null);
      setInterview(null);
      setToken(null);
      setRegion(null);
      setPlayState(false);
    };

    // Register cleanup for the reactive system
    onCleanup(dispose);

    // Return the store object
    return {
      userEmail,
      setUserEmail: persistedSetUserEmail,
      userInterviews,
      setUserInterviews: persistedSetUserInterviews,
      userAnswers,
      setUserAnswers: persistedSetUserAnswers,
      userFeedback,
      setUserFeedback: persistedSetUserFeedback,
      interview,
      setInterview: persistedSetInterview,
      token,
      setToken: persistedSetToken,
      region,
      setRegion: persistedSetRegion,
      playState,
      setPlayState: persistedSetPlayState,
      clearUserStore,
    };
  });
}

// Create a singleton store instance
const userStore = createUserStore();

// Export individual pieces from the store
export const {
  userEmail,
  setUserEmail,
  userInterviews,
  setUserInterviews,
  userAnswers,
  setUserAnswers,
  userFeedback,
  setUserFeedback,
  interview,
  setInterview,
  token,
  setToken,
  region,
  setRegion,
  playState,
  setPlayState,
  clearUserStore,
} = userStore;
